/**
 * SessionGate
 *
 * Replaces ProtectedRoute. Shows protected content only when session is active.
 * AuthFlowDialog is rendered in MainLayout, not here.
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSessionManager } from "../session-manager";
import { useBrowserWallet, useBrowserWalletStore } from "@/features/wallet";
import { Skeleton } from "@/components/ui/skeleton";
import { BrandButton } from "@/components/brand/BrandButton";
import { useTranslation } from "@/i18n";
import { Wallet } from "lucide-react";
import { WelcomeArrival, WelcomeView } from "@/components/brand/vnext";

export interface SessionGateProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  loadingComponent?: React.ReactNode;
  redirectTo?: string;
  redirectMessage?: string;
  welcome?: boolean;
}

function DefaultLoadingState() {
  return (
    <div className="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
      <Skeleton className="h-12 w-64" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function SessionGate({
  children,
  requireAuth = true,
  loadingComponent,
  redirectTo,
  redirectMessage = "Please authenticate to access this page",
  welcome = false,
}: SessionGateProps) {
  const { t } = useTranslation();
  const status = useSessionManager((s) => s.status);
  const previousStatus = useSessionManager((s) => s.previousStatus);
  const reconnectError = useSessionManager((s) => s.reconnectError);
  const reconnect = useSessionManager((s) => s.reconnect);
  const openModal = useBrowserWalletStore((s) => s.openModal);
  const initialized = useBrowserWalletStore((s) => s.initialized);
  const wallet = useBrowserWallet();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle redirect
  useEffect(() => {
    if (!requireAuth) return;
    if (status === "idle" && redirectTo) {
      navigate(redirectTo, {
        replace: true,
        state: {
          from: location.pathname + location.search,
          message: redirectMessage,
        },
      });
    }
  }, [status, requireAuth, redirectTo, redirectMessage, navigate, location]);

  // Auth not required — always render
  if (!requireAuth) {
    return <>{children}</>;
  }

  if (welcome && (status === "restoring" || status === "authenticating" && previousStatus !== "active" || status === "idle" && !redirectTo)) {
    return <WelcomeView
      state={status === "restoring" || !initialized ? "restoring" : status === "authenticating" ? "authenticating" : "ready"}
      onCreate={() => openModal("create")}
      onExisting={() => openModal()}
    />;
  }

  // Restoring — show loading
  if (status === "restoring") {
    return <>{loadingComponent || <DefaultLoadingState />}</>;
  }

  // Active — render protected content
  if (status === "active") {
    return welcome ? <WelcomeArrival>{children}</WelcomeArrival> : <>{children}</>;
  }

  // Authenticating from active (wallet switch) — keep content visible
  if (status === "authenticating" && previousStatus === "active") {
    return welcome ? <WelcomeArrival>{children}</WelcomeArrival> : <>{children}</>;
  }

  // Authenticating (fresh) — show loading while auth dialog is open
  if (status === "authenticating") {
    return <>{loadingComponent || <DefaultLoadingState />}</>;
  }

  // Wallet disconnected (ledger) — show reconnect prompt
  if (status === "wallet_disconnected") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold mb-2">
            {t("auth.walletDisconnected", "Wallet Disconnected")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {reconnectError
              ? reconnectError
              : t(
                  "auth.reconnectWalletDesc",
                  "Your wallet has been disconnected. Please reconnect to continue.",
                )}
          </p>
          <BrandButton
            variant="primary"
            size="lg"
            onClick={() => reconnect(wallet)}
            className="gap-2"
          >
            <Wallet className="w-5 h-5" />
            {t("auth.reconnectWallet", "Reconnect Wallet")}
          </BrandButton>
        </div>
      </div>
    );
  }

  // Idle — show login prompt or redirect
  if (redirectTo) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-semibold mb-2">
          {t("auth.walletRequired", "Authentication Required")}
        </h2>
        <p className="text-muted-foreground mb-6">{redirectMessage}</p>
        <BrandButton
          variant="primary"
          size="lg"
          onClick={() => openModal()}
          className="gap-2"
        >
          <Wallet className="w-5 h-5" />
          {t("auth.connectAndSignIn", "Connect & Sign In")}
        </BrandButton>
      </div>
    </div>
  );
}
